// ============================================================================
// PAYMENT SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { paymentEndpoints } from '../endpoints/payment.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  Payment,
  CreatePaymentRequest,
  UpdatePaymentRequest,
  PaymentSearchRequest,
} from '../types';

class PaymentService {
  async getAll(params?: PaymentSearchRequest): Promise<PaginatedResponse<Payment>> {
    const endpoint = params ? buildEndpointWithQuery('/Payment', params) : paymentEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw.data ?? {};

    return {
      success: raw.success,
      message: raw.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Payment>> {
    const response = await get<ApiResponse<Payment>>(paymentEndpoints.getById(id));
    return response.data;
  }

  async getByBookingId(bookingId: ID, includeVoided = false): Promise<ApiResponse<Payment[]>> {
    const endpoint = `${paymentEndpoints.getByBookingId(bookingId)}?includeVoided=${includeVoided}`;
    const response = await get<ApiResponse<Payment[]>>(endpoint);
    return response.data;
  }

  async create(data: CreatePaymentRequest): Promise<ApiResponse<Payment>> {
    const response = await post<ApiResponse<Payment>>(paymentEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdatePaymentRequest): Promise<ApiResponse<Payment>> {
    const response = await put<ApiResponse<Payment>>(paymentEndpoints.update(id), data);
    return response.data;
  }

  async void(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(paymentEndpoints.void(id), {
      params: { rowVersion },
    });
    return response.data;
  }
}

export const paymentService = new PaymentService();
export default paymentService;
